<template>
  <div class="frisbuy-select" :style="customStyle">
    <button
      class="frisbuy-select__select-button"
      :class="{ 'frisbuy-select__select-button--is-open': isSelectMenuOpen }"
      @click="selectMenuClickHandler"
    >
      <span value="">{{ selectedOption.text }} </span>

      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path
          d="M11.3749 4.45053L6.99992 8.71615L2.62492 4.45053C2.54296 4.36857 2.44729 4.32759 2.33792 4.32759C2.22854 4.32759 2.13287 4.36857 2.05092 4.45053C1.96896 4.53248 1.92798 4.62815 1.92798 4.73753C1.92798 4.8469 1.96444 4.93805 2.03735 5.01096L6.69935 9.55002C6.78131 9.63198 6.88165 9.67296 7.00035 9.67296C7.11906 9.67296 7.2194 9.63198 7.30135 9.55002L11.9634 5.02453C12.0363 4.94257 12.0727 4.84457 12.0727 4.73052C12.0727 4.61648 12.0317 4.52082 11.9498 4.44353C11.8678 4.36623 11.7722 4.32744 11.6628 4.32715C11.5534 4.32686 11.4578 4.36784 11.3758 4.45009L11.3749 4.45053Z"
          fill="#909399"
        />
      </svg>
    </button>

    <div
      class="frisbuy-select__options"
      :class="{ '--is-closed': !isSelectMenuOpen }"
      ref="selectOptions"
      v-click-outside="closeSelectMenu"
      tabindex="-1"
    >
      <span class="frisbuy-select__popper--arrow"></span>

      <div
        v-for="option in props.options"
        :key="option.value"
        class="frisbuy-select__option"
        @click="() => selectOption(option)"
      >
        <span class="frisbuy-select__option-text">
          {{ option.text }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
$select-menu-showing-animation-time: 0.1s;

.frisbuy-select {
  &,
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  position: relative;

  width: 100%;
  height: 100%;

  font-family: Inter, sans-serif;
  font-size: 16px;

  .frisbuy-select__select-button {
    display: flex;
    position: relative;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: space-between;
    align-items: center;

    background-color: #ffffff;

    border: 1px solid #dcdfe6;
    border-radius: 4px;

    width: 100%;
    height: 100%;

    padding: 5px 12px;

    span {
      color: #606266;
      font-size: 0.89rem;
    }

    svg {
      width: 14px;
      height: 14px;

      transition: transform $select-menu-showing-animation-time linear;
    }
  }

  .frisbuy-select__select-button--is-open {
    svg {
      transform: rotateZ(180deg);
    }
  }

  .frisbuy-select__options {
    display: flex;
    position: absolute;
    flex-direction: column;
    flex-wrap: nowrap;

    top: 100%;
    left: 0;

    width: 100%;

    background-color: #ffffff;

    border: 1px solid #e4e7ed;
    border-radius: inherit;

    transition-property: overflow, border-width, padding;
    transition-duration: $select-menu-showing-animation-time;
    transition-timing-function: linear;

    padding: 6px 0;
    margin: 12px 0 0 0;

    z-index: 3;

    &.--is-closed {
      overflow: hidden;

      border-width: 0;

      padding: 0;

      .frisbuy-select__popper--arrow {
        opacity: 0;
      }

      .frisbuy-select__option {
        height: 0;
        flex: 1 1 0;

        opacity: 0;
      }
    }

    .frisbuy-select__popper--arrow {
      display: block;
      position: absolute;
      width: 0;
      height: 0;
      border-color: transparent;
      border-style: solid;

      border-width: 6px;
      filter: drop-shadow(0 2px 12px rgba(0, 0, 0, 0.03));

      top: -6px;
      left: 50%;
      margin-right: 3px;
      border-top-width: 0;
      border-bottom-color: #ebeef5;

      left: 35px;

      transition-property: opacity;
      transition-duration: $select-menu-showing-animation-time;
      transition-timing-function: linear;

      &::after {
        display: block;
        position: absolute;
        width: 0;
        height: 0;
        border-color: transparent;
        border-style: solid;

        content: " ";
        border-width: 6px;

        top: 1px;
        margin-left: -6px;
        border-top-width: 0;
        border-bottom-color: #fff;
      }
    }

    .frisbuy-select__option {
      display: flex;
      align-items: center;
      flex: 1 1 34px;

      height: 34px;

      background-color: inherit;

      transition-property: background-color, height, flex, opacity;
      transition-duration: $select-menu-showing-animation-time;
      transition-timing-function: linear;

      padding: 0 16px;

      &:hover {
        background-color: #f5f7fa;
      }

      .frisbuy-select__option-text {
        color: #606266;
        font-size: 0.89rem;
      }
    }
  }
}
</style>

<script setup lang="ts">
import { HTMLAttributes, onMounted, ref, watch } from "vue";
import vClickOutside from "@/common/directives/vClickOutside";

type TSelectOption = {
  value: string | number | symbol | undefined;
  text: string;
};

const props = withDefaults(
  defineProps<{
    modelValue?: TSelectOption;

    options?: Array<TSelectOption>;
    defaultOption?: TSelectOption;
    customStyle?: HTMLAttributes["style"];
  }>(),
  {
    modelValue: () => {
      return { value: undefined, text: "" };
    },
  }
);

const emit = defineEmits<{
  "update:modelValue": [option: TSelectOption];
}>();

onMounted(() => {
  if (props.defaultOption) {
    setDefaultOption(props.defaultOption);
  }
});

const selectOptions = ref();
const isSelectMenuOpen = ref(false);

function openSelectMenu() {
  isSelectMenuOpen.value = true;

  selectOptions?.value.focus();
}

function closeSelectMenu() {
  isSelectMenuOpen.value = false;
}

function selectMenuClickHandler() {
  if (isSelectMenuOpen.value) {
    closeSelectMenu();
  } else {
    /* NOTE:
      vClickOutside without this timeout is called immediately
      after openSelectMenu via select-button's @click
    */
    setTimeout(() => {
      openSelectMenu();
    });
  }
}

function setDefaultOption(option: TSelectOption) {
  selectedOption.value = option;
}

const selectedOption = ref<TSelectOption>(props.modelValue);
function selectOption(option: TSelectOption) {
  emit("update:modelValue", option);

  selectedOption.value = option;
  closeSelectMenu();
}

watch(
  () => props.modelValue,
  (value) => {
    selectedOption.value = value;
  }
);
</script>
